import { useTranslation } from 'react-i18next';
import { Autocomplete, CircularProgress, createFilterOptions, TextField, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { Add, Search } from '@mui/icons-material';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { addOutOfStockItem, allOutOfStockItemsCacheKey } from '../../../apis/products-api';
import { OutOfStockItem } from '../OutOfStockUtils';
import {
  ItemTypeChip,
  OutOfStockCandidate,
  OutOfStockInfoBox,
  ScheduleDateField,
  useExternalShopId,
  useOutOfStockCandidates,
  useOutOfStockPermissions,
  useScheduleDates,
} from '../OutOfStockShared';
import { FormDialog } from '../../../design-system/Dialog';
import { getProductsApiError } from '../../../apis/products-api.errors';
import { useApiMutation } from '../../../apis/useApiMutation';

type Props = {
  open: boolean;
  onClose: () => void;
  existingOutOfStockItems: OutOfStockItem[];
};

/**
 * Matching is delegated to MUI so the options render in a popper: enough matches to scroll through
 * without dumping the whole catalog into it, and no silent "first 8 only" truncation of the list.
 */
const filterOptions = createFilterOptions<OutOfStockCandidate>({
  limit: 20,
  stringify: (option) => option.name,
});

const highlightOptions = { insideWords: true };

export const AddOutOfStockItemModal: React.FC<Props> = ({ open, onClose, existingOutOfStockItems }) => {
  const { t } = useTranslation();
  const { dateOptions } = useScheduleDates();

  const [selectedItem, setSelectedItem] = useState<OutOfStockCandidate | null>(null);
  // useScheduleDates resolves synchronously, so the first return date is available as the initial value.
  const [scheduledUntil, setScheduledUntil] = useState(() => dateOptions[0]?.value ?? '');

  const externalShopId = useExternalShopId();
  const { candidates, isLoading, isError } = useOutOfStockCandidates(existingOutOfStockItems);
  // The mutation's own isLoading is a render behind, so the guard reads this ref instead and only the
  // button's busy state is left to the observer.
  const isSubmittingRef = useRef(false);

  const addOutOfStock = useApiMutation(addOutOfStockItem, {
    getError: getProductsApiError,
    invalidateKeys: [[allOutOfStockItemsCacheKey]],
    successMessage: t('Marked as out of stock'),
    onSuccess: onClose,
    onSettled: () => {
      isSubmittingRef.current = false;
    },
  });

  const { canUpdate: userAuthorizesToUpdateOutOfStock } = useOutOfStockPermissions();

  // Cleared only once the dialog has animated away, so it does not visibly empty itself while closing.
  const handleExited = () => {
    setSelectedItem(null);
    setScheduledUntil(dateOptions[0]?.value ?? '');
  };

  const handleSubmit = () => {
    if (!selectedItem || !externalShopId || !userAuthorizesToUpdateOutOfStock || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    addOutOfStock.mutate({
      ...(selectedItem.type === 'ingredient' ? { ingredientId: selectedItem.id } : { productId: selectedItem.id }),
      externalShopId,
      scheduledUntil: dayjs(scheduledUntil).toISOString(),
    });
  };

  const canSubmit = Boolean(selectedItem) && userAuthorizesToUpdateOutOfStock;

  // Every section below stays mounted in every state: the matches render in the Autocomplete's popper,
  // so the dialog keeps the same height whether the field is empty, searching or holding a selection.
  return (
    <FormDialog
      open={open}
      onClose={onClose}
      TransitionProps={{ onExited: handleExited }}
      title={
        /* component="span": DialogTitle already renders the heading element. */
        <Typography variant="h3" component="span">
          {t('Select item to be removed')}
        </Typography>
      }
      onSubmit={handleSubmit}
      // One label for both item types: the button neither resizes nor contradicts an empty selection.
      submitLabel={t('Mark as out of stock')}
      cancelLabel={t('global.cancel')}
      isSubmitting={addOutOfStock.isLoading}
      submitDisabled={!canSubmit}
    >
      <OutOfStockInfoBox />
      <Autocomplete
        fullWidth
        autoHighlight
        blurOnSelect
        openOnFocus={false}
        forcePopupIcon={false}
        disabled={!userAuthorizesToUpdateOutOfStock}
        loading={isLoading}
        loadingText={t('Loading...')}
        noOptionsText={isError ? t('Loading the data failed') : t('No results')}
        // Empty while the menu availability resolves, so the popper says "loading" rather than
        // flashing candidates that the restaurant's menu filter is about to remove.
        options={isLoading ? [] : candidates}
        filterOptions={filterOptions}
        value={selectedItem}
        onChange={(_, value) => setSelectedItem(value)}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.type === value.type && option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            // A placeholder rather than a floating label: the shrunk label overhangs the input's top
            // edge, where DialogContent's scroll container clips it.
            placeholder={t('Search ingredient or product')}
            inputProps={{ ...params.inputProps, 'aria-label': t('Search ingredient or product') }}
            InputProps={{
              ...params.InputProps,
              // loadingText only shows once the popper is open; this says the catalog is still
              // arriving while the user is still deciding what to type.
              endAdornment: (
                <>
                  {isLoading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
              // The chip rides inside the field rather than on a row of its own, so picking an item
              // tells you whether it is an ingredient or a product without moving anything below.
              startAdornment: (
                <InputAdornments>
                  <Search color="action" />
                  {selectedItem && <ItemTypeChip type={selectedItem.type} />}
                </InputAdornments>
              ),
            }}
          />
        )}
        renderOption={(optionProps, option, { inputValue }) => {
          const parts = parse(option.name, match(option.name, inputValue, highlightOptions));
          return (
            <OptionRow {...optionProps} key={`${option.type}-${option.id}`}>
              <OptionName>
                {parts.map((part, index) => (
                  <MatchPart key={index} $highlight={part.highlight}>
                    {part.text}
                  </MatchPart>
                ))}
              </OptionName>
              <ItemTypeChip type={option.type} />
              <Add color="primary" />
            </OptionRow>
          );
        }}
      />
      <ScheduleDateField
        value={scheduledUntil}
        options={dateOptions}
        onChange={setScheduledUntil}
        disabled={!userAuthorizesToUpdateOutOfStock}
      />
    </FormDialog>
  );
};

const InputAdornments = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 8px;
`;

const OptionRow = styled.li`
  && {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const OptionName = styled.span`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const MatchPart = styled.span<{ $highlight: boolean }>`
  font-weight: ${({ $highlight }) => ($highlight ? 700 : 400)};
`;

export default AddOutOfStockItemModal;
